import { SyntheticEvent } from 'react';
import { observer } from 'mobx-react';
import cls from 'classnames';

import Button, { SIZE_SMALL } from '@/common/components/Button/Button';
import Icon from '@/common/components/Icon/Icon';
import Label from '@/common/components/Label/Label';
import Link from '@/common/components/Link/Link';
import RichText from '@/common/components/RichText/RichText';
import URLProvider from '@/common/services/URLProvider';
import Product from '@/product/entity/Product';
import { getDriveAccessAppearance } from '@/security/constants/access';

import Collection from '../../../entity/Collection';
import { useCollectionsStore } from '../../../stores/CollectionsStore';
import collectionsListStyles from '../CollectionsList.module.css';

interface CollectionsListItemProps {
  collection: Collection;
  productContext?: Nullable<Product>;
  onItemClick?: Nullable<(collection: Collection) => void>;
  withLinks?: boolean;
  onLinkClick?: () => void;
}

function CollectionsListItem(props: CollectionsListItemProps) {
  const { collection, productContext = null, onItemClick = null, withLinks = false, onLinkClick } = props;

  const collectionsStore = useCollectionsStore();
  const { activeCollection, remove, toggleActive } = collectionsStore;

  const isIdOfActiveCollection = Boolean(activeCollection && collection.id === activeCollection.id);

  function handleClick() {
    if (onItemClick) {
      onItemClick(collection);
    }
  }

  function handlePinClick(e: SyntheticEvent) {
    e.preventDefault();
    e.stopPropagation();
    toggleActive(collection);
  }

  function handleRemoveClick(e: SyntheticEvent) {
    e.preventDefault();
    e.stopPropagation();
    remove(collection);
  }

  return (
    <div
      aria-label="Collection item"
      className={cls(collectionsListStyles.wishCollectionsItem, {
        [collectionsListStyles.wishCollectionsItemActive]: isIdOfActiveCollection,
        'wish-collections__item--hoverable': Boolean(onItemClick),
        [collectionsListStyles.wishCollectionsItemDisabled]: Boolean(
          productContext && collection.hasProduct(productContext),
        ),
      })}
      onClick={handleClick}
    >
      <div className={collectionsListStyles.boardItem}>
        <Button
          aria-label="Pin collection"
          className={cls(collectionsListStyles.boardItemActiveCtrl, {
            [collectionsListStyles.boardItemInactiveCtrl]: !isIdOfActiveCollection,
          })}
          active={isIdOfActiveCollection}
          color={isIdOfActiveCollection ? 'default' : 'transparent'}
          onClick={handlePinClick}
          withIcon
          size={SIZE_SMALL}
        >
          <Icon name="pin-on" />
        </Button>

        <div className={collectionsListStyles.boardItemTitle}>
          {withLinks ? (
            <Link aria-label="Collection title" to={URLProvider.collectionDetailPage(collection)} onClick={onLinkClick}>
              {collection.title}
            </Link>
          ) : (
            <span aria-label="Collection title">{collection.title}</span>
          )}
        </div>

        {collection.description && (
          <div className={collectionsListStyles.boardItemDesc}>
            <RichText aria-label="Collection description" value={collection.description} />
          </div>
        )}

        <div className={collectionsListStyles.boardItemStatuses}>
          <Label
            aria-label="Collection access status"
            appearance={getDriveAccessAppearance(collection.access)}
            className={collectionsListStyles.boardItemStatus}
          >
            {collection.readableStatus}
          </Label>

          <span aria-label="Product count" className={collectionsListStyles.boardItemProductsAmount}>
            {collection.products ? collection.products.length : 0} product(s)
          </span>

          <span className={collectionsListStyles.boardItemSeparator}>|</span>

          <span aria-label="Collection creation date" className={collectionsListStyles.boardItemDate}>{collection.createdAtFormatted}</span>
        </div>

        <Button
          aria-label="Remove collection"
          color="transparent"
          size={SIZE_SMALL}
          className={collectionsListStyles.boardItemRemove}
          withIcon
          onClick={handleRemoveClick}
        >
          <Icon name="delete" />
        </Button>
      </div>
    </div>
  );
}

export default observer(CollectionsListItem);
